/**
 * Self-Service List Page (Zelf Halen & Brengen)
 *
 * Shows work orders where customers pick up or bring items themselves.
 * - "Op te halen" tab: Status = 'Wordt opgehaald' (customer picks up cleaned items)
 * - "Te brengen" tab: Status = 'Wordt gebracht' (customer brings items for cleaning)
 *
 * Features:
 * - Search by customer name or WO number
 * - Filter tabs (Ophalen/Brengen)
 * - Click to go to detail page with payment (for ophalen)
 * - Sorted alphabetically by customer last name
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { WbButton, WbChip, WbFlag, WbListRow, WbSearchField, WbSectionKop, WbState, WbToggleGroup } from '../components/wb';
import { useHeader } from '../stores';
import { salesforceClient } from '../lib/salesforce';
import type { SelfServiceWorkOrder } from '../lib/salesforce';
import { matchtZoek } from '../lib/utils/zoek';

// Polling interval for real-time updates (60 seconds)
const POLLING_INTERVAL_MS = 60000;

type TabFilter = 'ophalen' | 'brengen';

export function SelfServiceListPage() {
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [workOrders, setWorkOrders] = useState<SelfServiceWorkOrder[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState<TabFilter>('ophalen');

  // Fetch work orders from API
  const fetchWorkOrders = useCallback(async (showRefreshSpinner = false) => {
    try {
      if (showRefreshSpinner) {
        setIsRefreshing(true);
      } else {
        setIsLoading(true);
      }
      setError(null);

      const response = await salesforceClient.getSelfServiceWorkOrders(activeTab);
      console.log('[SelfServiceListPage] API response:', response);

      if (response.success && response.data) {
        setWorkOrders(response.data.workOrders || []);
      } else {
        setError(response.message || 'Kon werkorders niet ophalen');
        setWorkOrders([]);
      }
    } catch (err) {
      console.error('[SelfServiceListPage] Error fetching work orders:', err);
      setError(err instanceof Error ? err.message : 'Er ging iets mis bij het laden');
      setWorkOrders([]);
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, [activeTab]);

  // Fetch work orders on mount and when tab changes
  useEffect(() => {
    fetchWorkOrders();
  }, [fetchWorkOrders]);

  // Polling for real-time updates
  useEffect(() => {
    const interval = setInterval(() => {
      fetchWorkOrders(true);
    }, POLLING_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [fetchWorkOrders]);

  // Filter work orders by search query (client-side for instant feedback)
  const filteredWorkOrders = useMemo(() => {
    if (!searchQuery.trim()) {
      return workOrders;
    }

    return workOrders.filter(wo => matchtZoek(searchQuery, wo.customerName, wo.workOrderNumber));
  }, [workOrders, searchQuery]);

  // Handle tab change
  const handleTabChange = (tab: TabFilter) => {
    setActiveTab(tab);
    setSearchQuery(''); // Clear search when switching tabs
  };

  // Handle refresh
  const handleRefresh = async () => {
    await fetchWorkOrders(true);
  };

  // Format currency
  const formatCurrency = (amount: number | null) => {
    if (amount === null || amount === undefined) return '-';
    return `€${amount.toFixed(2).replace('.', ',')}`;
  };

  useHeader('Zelf halen en brengen', `${filteredWorkOrders.length}`);

  return (
    <div className="space-y-4">
      <WbToggleGroup<TabFilter>
        label="Soort"
        options={[
          { value: 'ophalen', label: 'Op te halen' },
          { value: 'brengen', label: 'Te brengen' },
        ]}
        value={activeTab}
        onChange={handleTabChange}
      />
      <WbSearchField value={searchQuery} onChange={setSearchQuery} inputMode="text" mono={false} placeholder="Zoek op klantnaam of WB-nummer" />

      {isLoading && <WbState variant="laden" titel="Werkorders laden…" />}
      {error && !isLoading && <WbState variant="fout" titel={error} onOpnieuw={() => fetchWorkOrders()} />}
      {!isLoading && !error && filteredWorkOrders.length === 0 && (
        <WbState
          variant="leeg"
          titel={searchQuery ? `Geen werkorders voor "${searchQuery}".` : activeTab === 'ophalen' ? 'Geen werkorders om op te halen.' : 'Geen werkorders om te ontvangen.'}
        />
      )}

      {!isLoading && !error && filteredWorkOrders.length > 0 && (
        <section className="space-y-1.5">
          <WbSectionKop
            label={activeTab === 'ophalen' ? 'Op te halen' : 'Te brengen'}
            teller={filteredWorkOrders.length}
            actie={
              <WbButton variant="link" onClick={handleRefresh} disabled={isRefreshing}>
                {isRefreshing ? 'Verversen…' : 'Ververs'}
              </WbButton>
            }
          />
          {filteredWorkOrders.map((wo) => {
            const open = activeTab === 'ophalen' && !wo.isFullyPaid && !!wo.openAmount && wo.openAmount > 0;
            return (
              <div key={wo.workOrderId} className="space-y-1">
                <WbListRow
                  onClick={() => navigate(`/self-service/${wo.workOrderId}`)}
                  tekst
                  hoofd={wo.customerName}
                  chips={
                    activeTab === 'ophalen'
                      ? (open ? <WbChip kind="actie">Open {formatCurrency(wo.openAmount)}</WbChip> : <WbChip kind="klaar">Betaald</WbChip>)
                      : <WbChip kind="wacht">Te ontvangen</WbChip>
                  }
                  sub={`${wo.workOrderNumber} · ${wo.itemCount} ${wo.itemCount === 1 ? 'item' : 'items'}${wo.totalM2 ? ` · ${wo.totalM2.toFixed(2).replace('.', ',')} m²` : ''}`}
                />
                {wo.notes && <WbFlag kind="opmerking">{wo.notes}</WbFlag>}
              </div>
            );
          })}
        </section>
      )}
    </div>
  );
}
